import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap-five'
import Nav from 'react-bootstrap-five/Nav'
import Tab from 'react-bootstrap-five/Tab'
import { history } from 'store'
import Item from './Item'
import TabGroup from './TabGroup'

// Vertical breathing room above/below a tab-group divider hairline (px).
const TAB_DIVIDER_MARGIN_PX = 8

export interface TabT {
  key: string
  title: string
  component?: React.ReactNode
  subtabs?: TabT[]
  link?: string // Optional link to navigate to a different page instead of using hash
  disabled?: boolean
  // When true, this pane unmounts while its tab is inactive (and isn't mounted
  // until first activated), so its local state fully resets on each (re)entry
  // instead of persisting across sibling-tab switches. Default (omitted) keeps
  // the pane mounted — the prior behavior — so existing consumers are unaffected.
  unmountInactive?: boolean
  // When true, a subtle grey20 hairline renders above this tab in the tablist —
  // a purely visual demarcation between tab groups. It's aria-hidden, so it never
  // pollutes the tablist's tab semantics. Default (omitted) → no divider, so
  // existing consumers are unaffected.
  dividerAbove?: boolean
}

type Props = {
  tabs: TabT[]
  // Optional accessible name for the vertical tablist (WCAG 1.3.1/4.1.2),
  // mirroring the horizontal TabNav's ariaLabel. Optional → existing callers
  // are unaffected.
  ariaLabel?: string
  // Controlled mode: when both are provided, `activeKey` drives the selected
  // tab and clicks call `onChange` instead of mutating the shared URL hash. This
  // lets InnerTabs render inside a modal or be re-homed without clobbering a
  // hash owned by an outer tab system (the BUGS-4407 failure mode). Omitting
  // both keeps the original uncontrolled hash behavior untouched.
  activeKey?: string
  onChange?: (key: string) => void
  // Layout of the tablist relative to its panes. 'sidebar' (default) keeps the
  // original md=2 sidebar Col + md=9 content Row. 'plain' renders the tablist
  // inline (no sidebar Col) with full-width content, for embedding in a modal
  // or a re-homed surface. Default preserves every existing consumer's layout.
  layout?: 'sidebar' | 'plain'
  // AI-1282: content that renders in the content column ABOVE the switched
  // Tab.Content, outside any one tab's own mount/unmount cycle — for a block
  // that stays visible (and mounted) no matter which tab is active, such as
  // Order feedback Settings' Feedback collection block. Omitted renders
  // nothing extra, so every existing consumer is unaffected.
  contentHeader?: React.ReactNode
}

const InnerTabs = ({
  tabs,
  ariaLabel,
  activeKey,
  onChange,
  layout = 'sidebar',
  contentHeader,
}: Props) => {
  const [css] = useStyletron()
  const isControlled = activeKey !== undefined && onChange !== undefined
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Controlled callers drive selection via activeKey, so the internal
    // hash-derived state is never read. Skip touching the shared hash entirely —
    // the F2 invariant is that a controlled InnerTabs never reads or writes it
    // (the BUGS-4407 clobber surface).
    if (isControlled) return activeKey ?? tabs[0]?.key ?? ''
    const param = history.location.hash.replace('#', '')
    const defaultTab =
      tabs.find(t => {
        if (t.disabled) return false
        if (t.key === param) return true
        return t.subtabs?.some(t => t.key === param)
      }) ||
      tabs.find(t => !t.disabled) ||
      tabs[0]

    if (defaultTab.subtabs) {
      const defaultSubtab =
        defaultTab.subtabs.find(t => t.key === param) || defaultTab.subtabs[0]
      return defaultSubtab.key
    }

    return defaultTab.key
  })

  // In controlled mode the caller owns selection, so `activeKey` is the source
  // of truth; otherwise fall back to the internal hash-driven state.
  const selected =
    isControlled && activeKey !== undefined ? activeKey : activeTab

  // Mount-only: scrub the hash only when it points at one of our own
  // disabled/unavailable tabs. Leave a hash we don't own alone — it may belong
  // to an outer tab system sharing this URL hash (e.g. Points Configuration
  // nested under the Points tabs). Controlled callers own navigation entirely,
  // so the scrub is skipped — InnerTabs never touches the hash when controlled.
  useEffect(() => {
    if (isControlled) return
    const param = history.location.hash.replace('#', '')
    if (!param) return

    const matchesHash = (tab: TabT): boolean =>
      tab.key === param || !!tab.subtabs?.some(s => s.key === param)
    const ownsHash = tabs.some(matchesHash)
    const honored = tabs.some(tab => !tab.disabled && matchesHash(tab))
    if (ownsHash && !honored) history.replace({ hash: activeTab })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Dev guard (AI-176 #1): controlled mode requires BOTH `activeKey` and
  // `onChange`. Supplying exactly one silently falls back to uncontrolled
  // (hash-driven) selection — almost always a call-site bug. Warn in dev only; a
  // discriminated-union prop type would be stricter but is a breaking signature
  // change for existing consumers, so the additive warning is the low-risk option
  // the ticket allows. Dev-gated, so the production `console.*` ban is honored.
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      (activeKey !== undefined) !== (onChange !== undefined)
    ) {
      // eslint-disable-next-line no-console
      console.error(
        '[InnerTabs] `activeKey` and `onChange` must be provided together for controlled mode; with only one supplied, InnerTabs falls back to uncontrolled (hash-driven) selection.'
      )
    }
  }, [activeKey, onChange])

  const setTab = tab => {
    // Find the tab to check if it has a link property
    const tabObj = tabs.find(t => t.key === tab)

    if (tabObj?.link) {
      // Navigate to the link instead of using hash
      history.push(tabObj.link)
    } else {
      // Use normal hash navigation
      history.replace({ hash: tab })
      setActiveTab(tab)
    }
  }

  // Controlled callers receive the key and own navigation (no hash write, no
  // link branch); uncontrolled callers keep the hash/link behavior in setTab.
  const handleSelect = tab => {
    if (!tab) return
    if (isControlled) {
      onChange?.(tab)
      return
    }
    setTab(tab)
  }

  const tablist = (
    <Nav className="flex-column border-none" aria-label={ariaLabel}>
      {tabs.map(tab => (
        <React.Fragment key={tab.key}>
          {tab.dividerAbove && (
            <hr
              aria-hidden="true"
              className={css({
                height: '0px',
                // Independent border shorthands (width/style/color) — no `border`
                // master shorthand — so only the top edge draws a hairline
                // regardless of the UA/Bootstrap <hr> defaults.
                borderWidth: '1px 0 0 0',
                borderStyle: 'solid',
                borderColor: palette.grey20,
                opacity: 1,
                margin: `${TAB_DIVIDER_MARGIN_PX}px 0`,
              })}
            />
          )}
          <Switch
            condition={!tab.subtabs}
            fallback={<TabGroup tab={tab} activeTab={selected} />}
          >
            <Item
              className="px-m border-radius-5"
              tabKey={tab.key}
              title={tab.title}
              isActive={tab.key === selected}
              disabled={tab.disabled}
            />
          </Switch>
        </React.Fragment>
      ))}
    </Nav>
  )

  const content = (
    <>
      {contentHeader}
      <Tab.Content>
        {tabs.map(({ key, component, subtabs, unmountInactive }) => (
          <React.Fragment key={key}>
            <Switch condition={!!component}>
              <Tab.Pane
                key={key}
                className="grey-05-bkg"
                eventKey={key}
                mountOnEnter={unmountInactive}
                unmountOnExit={unmountInactive}
              >
                {component}
              </Tab.Pane>
            </Switch>
            {subtabs?.map(
              ({ key, component, unmountInactive: subUnmountInactive }) => (
                <Tab.Pane
                  key={key}
                  className="grey-05-bkg"
                  eventKey={key}
                  mountOnEnter={subUnmountInactive}
                  unmountOnExit={subUnmountInactive}
                >
                  {component}
                </Tab.Pane>
              )
            )}
          </React.Fragment>
        ))}
      </Tab.Content>
    </>
  )

  return (
    <Tab.Container
      activeKey={selected}
      transition={false}
      onSelect={handleSelect}
    >
      <Switch
        condition={layout === 'plain'}
        fallback={
          <Row className="mt-m">
            <Col className="p-0" md="2">
              {tablist}
            </Col>
            <Col className="pl-xl" md="9">
              {content}
            </Col>
          </Row>
        }
      >
        {tablist}
        {content}
      </Switch>
    </Tab.Container>
  )
}

export default InnerTabs
